// API клиент.
//
// Адресът се намира сам, за да не се пипа при всяка смяна на мрежа:
//  1. EXPO_PUBLIC_API_URL — ако е зададен (продукция или ръчно).
//  2. Иначе при разработка: IP-то, от което Expo сервира приложението.
//     Телефонът вече е свързан с този адрес, значи го достига и за API-то.
//  3. Иначе localhost (уеб/симулатор).
// Така при нова мрежа Metro си взима новото IP и приложението го следва.

import Constants from 'expo-constants';
import { Platform } from 'react-native';
import { acceptLanguage } from './i18n';

const API_PORT = 4000;

function resolveApiUrl(): string {
  const fromEnv = process.env.EXPO_PUBLIC_API_URL;
  if (fromEnv) return fromEnv;

  // напр. "192.168.0.10:8081" — хостът, от който Expo Go е заредил приложението
  const hostUri = Constants.expoConfig?.hostUri ?? (Constants as any).expoGoConfig?.debuggerHost;
  const host = typeof hostUri === 'string' ? hostUri.split(':')[0] : null;
  if (host && host !== 'localhost' && host !== '127.0.0.1') {
    return `http://${host}:${API_PORT}`;
  }
  return `http://localhost:${API_PORT}`;
}

export const API_URL = resolveApiUrl();

let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

// Тихо подновяване на сесията при изтекъл access токен (той живее 15 мин).
// Без това приложението „замръзва" след престой на заден план — всички
// заявки връщат 401. AuthContext регистрира функцията при старт.
let refreshHandler: (() => Promise<string | null>) | null = null;
let refreshing: Promise<string | null> | null = null;

export function setRefreshHandler(fn: (() => Promise<string | null>) | null): void {
  refreshHandler = fn;
}

async function tryRefresh(): Promise<string | null> {
  if (!refreshHandler) return null;
  // едновременните 401-ци споделят ЕДНО подновяване (без надпревара)
  refreshing ??= refreshHandler().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

export async function api<T = any>(
  path: string,
  options: { method?: string; body?: unknown } = {}
): Promise<T> {
  const doFetch = () =>
    fetch(`${API_URL}${path}`, {
      method: options.method ?? (options.body !== undefined ? 'POST' : 'GET'),
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': acceptLanguage(),
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    });

  let res = await doFetch();

  // Изтекъл токен → подновяваме и повтаряме ВЕДНЪЖ (не за самите /auth пътища)
  if (res.status === 401 && !path.startsWith('/auth/')) {
    const renewed = await tryRefresh();
    if (renewed) res = await doFetch();
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const body = data as { error?: string; message?: string };
    const err = new Error(body.error ?? body.message ?? `Грешка ${res.status}`);
    // Допълнителните полета от отговора (напр. code, limit, needProductId при
    // APPLE_LIMIT) остават достъпни за екрана, който хваща грешката.
    if (data && typeof data === 'object') Object.assign(err, data, { message: err.message });
    throw err;
  }
  return data as T;
}

// Качва локален файл (от image picker) и връща URL за записване в базата.
// Телефон: файлът се подава като {uri, name, type}; браузър: истински Blob.
export async function uploadFile(localUri: string): Promise<string> {
  const form = new FormData();

  if (Platform.OS === 'web') {
    const blob = await (await fetch(localUri)).blob();
    const ext = (blob.type.split('/')[1] ?? 'jpg').replace('jpeg', 'jpg');
    (form as unknown as { append(name: string, value: Blob, fileName?: string): void }).append(
      'file',
      blob,
      `photo.${ext}`
    );
  } else {
    const name = localUri.split('/').pop() ?? 'photo.jpg';
    const ext = name.includes('.') ? name.split('.').pop()!.toLowerCase() : 'jpg';
    form.append('file', {
      uri: localUri,
      name,
      type: ext === 'png' ? 'image/png' : 'image/jpeg',
    } as unknown as Blob);
  }

  const doFetch = () =>
    fetch(`${API_URL}/files`, {
      method: 'POST',
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
      body: form,
    });

  let res = await doFetch();
  if (res.status === 401) {
    const renewed = await tryRefresh();
    if (renewed) res = await doFetch();
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error ?? 'Качването се провали.');
  return (data as { url: string }).url;
}

// Multipart качване на НЯКОЛКО файла към конкретен ендпойнт (напр.
// верификацията на криейтър, която не минава през общото /files хранилище).
export async function uploadForm(path: string, files: { field: string; uri: string }[]): Promise<any> {
  const form = new FormData();
  for (const f of files) {
    if (Platform.OS === 'web') {
      const blob = await (await fetch(f.uri)).blob();
      (form as unknown as { append(name: string, value: Blob, fileName?: string): void }).append(f.field, blob, `${f.field}.jpg`);
    } else {
      const name = f.uri.split('/').pop() ?? `${f.field}.jpg`;
      const ext = name.includes('.') ? name.split('.').pop()!.toLowerCase() : 'jpg';
      form.append(f.field, { uri: f.uri, name, type: ext === 'png' ? 'image/png' : 'image/jpeg' } as unknown as Blob);
    }
  }

  const doFetch = () =>
    fetch(`${API_URL}${path}`, {
      method: 'POST',
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
      body: form,
    });
  let res = await doFetch();
  if (res.status === 401) {
    const renewed = await tryRefresh();
    if (renewed) res = await doFetch();
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error ?? 'Качването се провали.');
  return data;
}

export const money = (cents: number) => `${(cents / 100).toFixed(2)} €`;

export const monthLabel = (m: number, y: number) => `${String(m).padStart(2, '0')}.${y}`;

export const UTILITY_LABELS: Record<string, string> = {
  ELECTRICITY: 'Ток',
  WATER: 'Вода',
  BUILDING_FEE: 'Такса вход',
  GAS: 'Газ',
  INTERNET: 'Интернет',
  OTHER: 'Друго',
};

export const ISSUE_STATUS_LABELS: Record<string, string> = {
  SUBMITTED: 'Подадена',
  SEEN: 'Видяна',
  ASSIGNED: 'Възложена',
  RESOLVED: 'Отстранена',
  CONFIRMED: 'Потвърдена',
};

export const PAYMENT_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Дължимо',
  AWAITING_CONFIRMATION: 'Чака потвърждение',
  SUBMITTED: 'Наредено',
  COLLECTED: 'Изтеглено',
  IN_TRANSIT: 'Пътува',
  PAID: 'Платено',
  FAILED: 'Неуспешно',
  RETURNED: 'Върнато',
  CANCELED: 'Отпаднало (прекратен наем)',
};
